import React, { useRef, useState } from "react";
import { Share2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Renders a MAVULA-branded 1200x630 image on canvas with the visitor's
 * personal impact figures. Uses Web Share API when available, falls back to download.
 */
export default function ShareImpactButton({ amount, currency, term, homes, jobs, training, wages }) {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const money = (v) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: (currency || "GBP").toUpperCase(), maximumFractionDigits: 0 }).format(v || 0);

  const draw = () => {
    const c = canvasRef.current || document.createElement("canvas");
    c.width = 1200; c.height = 630;
    const ctx = c.getContext("2d");

    // Background gradient
    const g = ctx.createLinearGradient(0, 0, 1200, 630);
    g.addColorStop(0, "#0A3B28");
    g.addColorStop(1, "#062418");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 1200, 630);
    // Gold radial highlight
    const rg = ctx.createRadialGradient(220, 100, 20, 220, 100, 600);
    rg.addColorStop(0, "rgba(212,175,55,0.4)");
    rg.addColorStop(1, "rgba(212,175,55,0)");
    ctx.fillStyle = rg;
    ctx.fillRect(0, 0, 1200, 630);

    // Eyebrow
    ctx.fillStyle = "#D4AF37";
    ctx.font = "600 22px sans-serif";
    ctx.fillText("§ MY MAVULA IMPACT", 80, 100);

    // Main line
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "700 56px serif";
    ctx.fillText(`With ${money(amount)}`, 80, 190);
    ctx.font = "italic 500 44px serif";
    ctx.fillStyle = "#F8D57E";
    ctx.fillText(`over ${term} years, I build:`, 80, 250);

    // 4 impact numbers in a grid
    const items = [
      { n: homes,    l: "freehold homes" },
      { n: jobs,     l: "local jobs supported" },
      { n: training, l: "training places" },
      { n: money(wages), l: "into local wages" },
    ];
    ctx.font = "700 62px sans-serif";
    for (let i = 0; i < items.length; i++) {
      const col = i % 2, row = Math.floor(i / 2);
      const x = 80 + col * 540, y = 360 + row * 130;
      ctx.fillStyle = "#F8D57E";
      ctx.font = "700 62px sans-serif";
      ctx.fillText(String(items[i].n), x, y);
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "400 22px sans-serif";
      ctx.fillText(items[i].l, x, y + 34);
    }

    // Footer
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "italic 500 26px serif";
    ctx.fillText("mavula · pulalove.com", 80, 600);
    ctx.fillStyle = "#D4AF37";
    ctx.font = "700 22px sans-serif";
    ctx.fillText("MAVULA · OPEN DOORS", 900, 600);

    return c;
  };

  const share = async () => {
    setBusy(true);
    try {
      const c = draw();
      const blob = await new Promise((r) => c.toBlob(r, "image/png"));
      const file = new File([blob], "mavula-my-impact.png", { type: "image/png" });
      const shareData = {
        title: "My MAVULA Impact",
        text: `With ${money(amount)} over ${term} yrs I build ${homes} homes, ${jobs} jobs and ${training} training places with MAVULA.`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        return;
      }
      // fallback: download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "mavula-my-impact.png"; document.body.appendChild(a);
      a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Image downloaded — share it on WhatsApp, Instagram or LinkedIn.");
    } catch (e) {
      // AbortError is normal if user cancels the share sheet
      if (e?.name !== "AbortError") toast.error("Could not create share image.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={share} disabled={busy} className="btn-gold" data-testid="share-impact-btn">
        {busy ? <Loader2 size={16} className="animate-spin"/> : <Share2 size={16}/>}
        {busy ? "Preparing…" : "Share your impact"}
      </button>
      <canvas ref={canvasRef} className="hidden" width={1200} height={630} aria-hidden="true"/>
    </>
  );
}
